// Goblic's own MCP server on the settings page: the workspace switch that
// opens it to external tools, and the signed-in user's API tokens for it.

import { useState, type FormEvent } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  ApiError,
  apiTokensApi,
  oauthApi,
  getApiBaseUrl,
  workspaceApi,
  type CreatedApiToken,
} from '../lib/api-client'
import { useAuth } from '../lib/auth'
import { formatRelative } from '../lib/format'
import { useDialogs } from './dialogs'
import { Field, LoadingRows, Notice, Panel } from './ui'

function CopyBlock({ label, value }: { label: string; value: string }) {
  const [copied, setCopied] = useState(false)
  return (
    <div>
      <div className="mb-1 flex items-center justify-between gap-3">
        <span className="text-sm font-medium text-ink-2">{label}</span>
        <button
          type="button"
          className="text-sm text-ink-3 hover:text-ink"
          onClick={() => {
            void navigator.clipboard.writeText(value).then(() => {
              setCopied(true)
              window.setTimeout(() => setCopied(false), 1500)
            })
          }}
        >
          {copied ? 'Copied' : 'Copy'}
        </button>
      </div>
      <pre className="overflow-x-auto whitespace-pre-wrap break-all rounded-control border border-line bg-sunken px-3 py-2 font-mono text-sm text-ink">
        {value}
      </pre>
    </div>
  )
}

export function GoblicMcpPanel({
  workspaceId,
  externalApiAccess,
}: {
  workspaceId: string
  externalApiAccess: boolean
}) {
  const queryClient = useQueryClient()
  const { alert, confirm } = useDialogs()
  const { user } = useAuth()
  const isOwner = user?.roles.includes('workspace_owner') ?? false
  const mcpUrl = `${getApiBaseUrl()}/mcp`

  const [name, setName] = useState('')
  const [scope, setScope] = useState<'full' | 'read'>('full')
  const [created, setCreated] = useState<CreatedApiToken | null>(null)

  const tokensQuery = useQuery({
    queryKey: ['api-tokens'],
    queryFn: () => apiTokensApi.list(),
  })

  const connectionsQuery = useQuery({
    queryKey: ['oauth-connections'],
    queryFn: () => oauthApi.listConnections(),
  })
  const disconnect = useMutation({
    mutationFn: (id: string) => oauthApi.revokeConnection(id),
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: ['oauth-connections'] }),
    onError: (error) =>
      void alert({
        kind: 'error',
        title: 'Couldn’t disconnect the app',
        message: error instanceof ApiError ? error.message : undefined,
      }),
  })

  const toggle = useMutation({
    mutationFn: (next: boolean) =>
      workspaceApi.update(workspaceId, { externalApiAccess: next }),
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: ['workspace', workspaceId] }),
    onError: (error) =>
      void alert({
        kind: 'error',
        title: 'Couldn’t change external access',
        message: error instanceof ApiError ? error.message : undefined,
      }),
  })

  const create = useMutation({
    mutationFn: () => apiTokensApi.create({ name: name.trim(), scope }),
    onSuccess: (token) => {
      setCreated(token)
      setName('')
      void queryClient.invalidateQueries({ queryKey: ['api-tokens'] })
    },
    onError: (error) =>
      void alert({
        kind: 'error',
        title: 'Couldn’t create the token',
        message: error instanceof ApiError ? error.message : undefined,
      }),
  })

  const revoke = useMutation({
    mutationFn: (id: string) => apiTokensApi.revoke(id),
    onSuccess: (_result, id) => {
      if (created?.id === id) setCreated(null)
      void queryClient.invalidateQueries({ queryKey: ['api-tokens'] })
    },
  })

  function onCreate(event: FormEvent) {
    event.preventDefault()
    if (name.trim()) create.mutate()
  }

  const tokens = tokensQuery.data?.data ?? []
  const connections = connectionsQuery.data?.data ?? []

  return (
    <Panel
      title="Goblic MCP"
      description="Let an AI tool like Claude Code, Cursor or Codex set up projects and agents here, with your rights."
      flush
    >
      <ul className="divide-y divide-line">
        <li className="flex items-center justify-between gap-4 px-5 py-3">
          <label htmlFor="external-api-access" className="min-w-0">
            <span className="block text-base font-medium text-ink">
              Access for external tools
            </span>
            <span className="block text-sm text-ink-3">
              {isOwner
                ? 'Off keeps the MCP server closed: tokens stop working until you turn it on.'
                : 'Only the workspace owner can change this.'}
            </span>
          </label>
          <span className="flex shrink-0 items-center gap-2.5">
            <span className="text-sm text-ink-2">
              {externalApiAccess ? 'On' : 'Off'}
            </span>
            <input
              id="external-api-access"
              type="checkbox"
              role="switch"
              className="switch"
              checked={externalApiAccess}
              disabled={!isOwner || toggle.isPending}
              onChange={(event) => toggle.mutate(event.target.checked)}
            />
          </span>
        </li>

        <li className="space-y-4 px-5 py-4">
          {created ? (
            <div className="space-y-3">
              <Notice tone="success" onDismiss={() => setCreated(null)}>
                Token “{created.name}” is ready. Copy it now: Goblic won’t show
                it again.
              </Notice>
              <CopyBlock label="Token" value={created.token} />
              <CopyBlock
                label="Add to Claude Code"
                value={`claude mcp add --transport http goblic ${mcpUrl} --header "Authorization: Bearer ${created.token}"`}
              />
              <CopyBlock
                label="Or in an mcp.json (Cursor and others)"
                value={JSON.stringify(
                  {
                    mcpServers: {
                      goblic: {
                        url: mcpUrl,
                        headers: { Authorization: `Bearer ${created.token}` },
                      },
                    },
                  },
                  null,
                  2
                )}
              />
            </div>
          ) : (
            <CopyBlock label="Server address" value={mcpUrl} />
          )}

          <form
            onSubmit={onCreate}
            className="flex flex-wrap items-end gap-3"
          >
            <div className="min-w-[12rem] flex-1">
              <Field label="New token" htmlFor="api-token-name">
                <input
                  id="api-token-name"
                  className="field-input"
                  placeholder="e.g. Claude Code on my laptop"
                  value={name}
                  maxLength={80}
                  onChange={(event) => setName(event.target.value)}
                />
              </Field>
            </div>
            <select
              aria-label="What the token may do"
              className="field-input w-auto"
              value={scope}
              onChange={(event) =>
                setScope(event.target.value as 'full' | 'read')
              }
            >
              <option value="full">Read and change</option>
              <option value="read">Read only</option>
            </select>
            <button
              type="submit"
              className="btn-primary"
              disabled={!name.trim() || create.isPending}
            >
              Create token
            </button>
          </form>
        </li>

        {tokensQuery.isLoading ? (
          <li>
            <LoadingRows />
          </li>
        ) : (
          tokens.map((token) => (
            <li
              key={token.id}
              className="flex items-center justify-between gap-4 px-5 py-3"
            >
              <span className="min-w-0">
                <span className="block text-base font-medium text-ink">
                  {token.name}
                </span>
                <span className="block text-sm text-ink-3">
                  <span className="font-mono">{token.prefix}…</span> ·{' '}
                  {token.scope === 'read' ? 'Read only' : 'Read and change'} ·{' '}
                  {token.lastUsedAt
                    ? `used ${formatRelative(token.lastUsedAt)}`
                    : 'never used'}
                </span>
              </span>
              <button
                type="button"
                className="btn-secondary btn-sm shrink-0"
                disabled={revoke.isPending}
                onClick={async () => {
                  if (
                    await confirm({
                      kind: 'delete',
                      title: `Revoke “${token.name}”?`,
                      message:
                        'Tools using this token lose access right away.',
                      confirmLabel: 'Revoke',
                    })
                  )
                    revoke.mutate(token.id)
                }}
              >
                Revoke
              </button>
            </li>
          ))
        )}

        {connections.length > 0 ? (
          <li className="bg-sunken px-5 py-2 text-sm font-medium text-ink-2">
            Connected apps
          </li>
        ) : null}
        {connections.map((connection) => (
          <li
            key={connection.id}
            className="flex items-center justify-between gap-4 px-5 py-3"
          >
            <span className="min-w-0">
              <span className="block text-base font-medium text-ink">
                {connection.clientName}
              </span>
              <span className="block text-sm text-ink-3">
                {connection.workspaceName} ·{' '}
                {connection.scope === 'mcp:read' ? 'Read only' : 'Read and change'}{' '}
                ·{' '}
                {connection.lastUsedAt
                  ? `used ${formatRelative(connection.lastUsedAt)}`
                  : 'never used'}
              </span>
            </span>
            <button
              type="button"
              className="btn-secondary btn-sm shrink-0"
              disabled={disconnect.isPending}
              onClick={async () => {
                if (
                  await confirm({
                    kind: 'delete',
                    title: `Disconnect “${connection.clientName}”?`,
                    message: 'The app loses access right away.',
                    confirmLabel: 'Disconnect',
                  })
                )
                  disconnect.mutate(connection.id)
              }}
            >
              Disconnect
            </button>
          </li>
        ))}
      </ul>
    </Panel>
  )
}
